import DatePicker from '@risk-smart/themed-cloudscape-components/date-picker';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import SimpleDateInput from '@risksmart-app/components/src/form/simple-date-input/SimpleDateInput';
import dayjs from 'dayjs';
import type { ReactNode } from 'react';
import type { FieldValues, Noop, RefCallBack } from 'react-hook-form';
import { FormField } from 'src/components/form/form/FormField';
import type { Content } from 'src/components/help-panel/useHelpStore';

import { Controller } from '../field-controller/Controller';
import { useIsFieldReadOnly } from '../form/customisable-form/hooks/useIsFieldReadOnly';
import type { ControlledBaseProps } from '../types';
import styles from './style.module.scss';

const isTodayOrBefore = (date: Date): boolean =>
  dayjs(date).startOf('day').valueOf() <= dayjs().startOf('day').valueOf();

interface DatePickerInputWithFormFieldProps extends DatePickerInputProps {
  label: string;
  /**
   * Which calendar days may be picked. Reaches the underlying DatePicker
   * untouched, so it wins over `disableFutureDates` if both are supplied.
   */
  isDateEnabled?: (date: Date) => boolean;
  errorMessage?: string;
  testId?: string;
  info?: ReactNode;
  disableBottomPadding?: boolean;
  guidance?: Content | undefined;
}

interface DatePickerInputProps {
  onChange?: (value: string) => void;
  value?: string;
  onBlur?: Noop;
  locale?: string;
  disableFutureDates?: boolean;
  disabled?: boolean;
  innerRef?: RefCallBack;
  placeholder?: string;
  // Forwarded to Cloudscape via `rest`. Needed when the picker renders inside a
  // popover that would otherwise clip the calendar.
  expandToViewport?: boolean;
}

export const DatePickerInput = ({
  value = '',
  onChange,
  disabled,
  onBlur,
  innerRef,
  placeholder,
  disableFutureDates,
  ...rest
}: DatePickerInputProps) => {
  const valueToIso = (val: string) => (val ? new Date(val).toISOString() : '');

  return (
    <SpaceBetween direction={'horizontal'} size={'xs'} alignItems={'center'}>
      <SimpleDateInput
        value={value}
        onChange={(val) => {
          onChange?.(valueToIso(val));
        }}
        onBlur={onBlur}
        disabled={disabled}
      />
      <DatePicker
        {...{ className: styles.hideDateInput }}
        ref={innerRef}
        onBlur={onBlur}
        onChange={(e) => {
          onChange?.(valueToIso(e.detail.value));
        }}
        value={dayjs(value || Date.now()).format('YYYY-MM-DD')}
        nextMonthAriaLabel={'Next month'}
        placeholder={placeholder || 'DD/MM/YYYY'}
        previousMonthAriaLabel={'Previous month'}
        todayAriaLabel={'Today'}
        disabled={disabled}
        {...(disableFutureDates ? { isDateEnabled: isTodayOrBefore } : {})}
        {...rest}
      />
    </SpaceBetween>
  );
};

export const DatePickerInputWithFormField = ({
  label,
  value = '',
  onChange,
  errorMessage,
  disabled,
  onBlur,
  innerRef,
  placeholder,
  testId,
  info,
  disableBottomPadding,
  disableFutureDates,
  ...rest
}: DatePickerInputWithFormFieldProps) => {
  const valueToIso = (val: string) => (val ? new Date(val).toISOString() : '');

  return (
    <FormField
      info={info}
      label={label}
      errorText={errorMessage}
      stretch
      testId={testId}
      disableBottomPadding={disableBottomPadding}
    >
      <SpaceBetween direction={'horizontal'} size={'xs'} alignItems={'center'}>
        <SimpleDateInput
          value={value}
          testId={testId}
          onChange={(val) => {
            onChange?.(valueToIso(val));
          }}
          onBlur={onBlur}
          disabled={disabled}
        />
        <DatePicker
          {...{ className: styles.hideDateInput }}
          ref={innerRef}
          onBlur={onBlur}
          onChange={(e) => {
            onChange?.(valueToIso(e.detail.value));
          }}
          value={dayjs(value || Date.now()).format('YYYY-MM-DD')}
          nextMonthAriaLabel={'Next month'}
          placeholder={placeholder || 'DD/MM/YYYY'}
          previousMonthAriaLabel={'Previous month'}
          todayAriaLabel={'Today'}
          disabled={disabled}
          {...(disableFutureDates ? { isDateEnabled: isTodayOrBefore } : {})}
          {...rest}
        />
      </SpaceBetween>
    </FormField>
  );
};

interface Props<T extends FieldValues> extends ControlledBaseProps<T> {
  locale?: string;
  disabled?: boolean;
  disableFutureDates?: boolean;
  isDateEnabled?: (date: Date) => boolean;
  onChange?: (value: string) => void;
  expandToViewport?: boolean;
  testId: string;
}

export const ControlledDatePicker = <T extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  disabled,
  disableFutureDates,
  onChange,
  forceRequired,
  defaultRequired,
  allowDefaultValue,
  testId,
  description,
  ...rest
}: Props<T>) => {
  const { error } = control.getFieldState(name);
  const readOnly = useIsFieldReadOnly(name);

  return (
    <Controller
      forceRequired={forceRequired}
      defaultRequired={defaultRequired}
      allowDefaultValue={allowDefaultValue}
      name={name}
      control={control}
      render={({ field: { ref, onChange: formOnChange, onBlur, value } }) => (
        <DatePickerInputWithFormField
          guidance={description}
          label={label}
          onChange={(val: string) => {
            formOnChange(val);
            onChange?.(val);
          }}
          testId={testId}
          onBlur={onBlur}
          disabled={disabled || readOnly}
          disableFutureDates={disableFutureDates}
          value={value}
          innerRef={ref}
          placeholder={placeholder}
          errorMessage={error?.message}
          {...rest}
        />
      )}
    />
  );
};
